/* eslint-disable no-restricted-imports */
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useInventory } from '@/data/Content/_Inventory';
import {
	addToCartFetcher,
	BASE_ADD_2_CART_BODY,
	promoCodeApplicator,
	useCart,
} from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useProducts } from '@/data/Content/Products';
import { generateOrderItem, tallaOrder } from '@/data/Content/Outfits';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { EMAIL_CAMPAIGN_QUERY_KEYS } from '@/data/Content/EmailCampaignsAddingProducts';
import { useRemoveQueryParams } from 'integration/hooks/useRemoveQueryParams';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { useUser } from '@/data/User';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { processError } from '@/data/utils/processError';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

type UseEmailRecommendedCouponPopupParams = {
	partNumbers?: string[];
	couponCode?: string;
	onClose?: () => void;
};

type DictionaryChildren = Record<string, { partNumber: string; productId: string }[]>;

type ProductSelection = Record<string, string | null>;

export const useEmailRecommendedCouponPopup = ({
	partNumbers,
	couponCode,
	onClose,
}: UseEmailRecommendedCouponPopupParams) => {
	const [productsSelected, setProductsSelected] = useState<ProductSelection>({});
	const { notifyError } = useNotifications();
	const { pickupInStoreShipMode } = useAllowableShippingModes();
	const { settings } = useSettings();
	const { mutateCart } = useCart();
	const params = useExtraRequestParameters();
	const router = useNextRouter();
	const localRoutes = useLocalization('Routes');
	const openAuthCheckoutFlow = useIsOpen();
	const { user: { isLoggedIn = false } = {} } = useUser();

	const { products: fetchedProducts = [], loading: isLoadingProducts } = useProducts({
		partNumber: partNumbers,
	});

	const products = useMemo(() => {
		if (!partNumbers || partNumbers.length === 0) {
			return fetchedProducts;
		}

		return partNumbers
			.map((partNumber) => fetchedProducts.find((p) => p.partNumber === partNumber))
			.filter((product): product is NonNullable<typeof product> => product !== undefined);
	}, [fetchedProducts, partNumbers]);

	const dictionaryChildrenIds = useMemo(
		() =>
			products.reduce<DictionaryChildren>((acc, product) => {
				const productPartNumbers =
					product.items?.flatMap((variation) =>
						variation.partNumber
							? [{ partNumber: variation.partNumber, productId: product.id }]
							: []
					) || [];
				return { ...acc, [product.partNumber]: productPartNumbers };
			}, {}),
		[products]
	);

	const childrenPartNumbers = useMemo(
		() =>
			Object.values(dictionaryChildrenIds)
				.map((arr) => arr.map((item) => item.partNumber))
				.flat()
				.toString(),
		[dictionaryChildrenIds]
	);

	const { availability = [], loading: isLoadingAvailability } = useInventory(childrenPartNumbers);

	const productsWithSizesFiltered = useMemo(
		() =>
			products
				.map((product) => {
					if (!product.items.length || !availability.length) {
						return { ...product, sizes: [] };
					}

					const sizes = product.items
						.reduce<{ partNumber: string; size: string }[]>((acc, item) => {
							const availItem = availability.find((avail) => avail.productId === item.id);

							if (!availItem?.status) {
								return acc;
							}

							const size = findProductAttribute(item.attributes, 'Talla')?.value;

							if (size) {
								acc.push({ partNumber: item.partNumber, size });
							}
							return acc;
						}, [])
						.sort((a, b) => {
							const sizeALower = a.size.toLowerCase();
							const sizeBLower = b.size.toLowerCase();

							if (tallaOrder[sizeALower] && tallaOrder[sizeBLower]) {
								return tallaOrder[sizeALower] - tallaOrder[sizeBLower];
							}

							if (tallaOrder[sizeALower]) return -1;
							if (tallaOrder[sizeBLower]) return 1;

							const numA = Number(a.size);
							const numB = Number(b.size);

							if (!isNaN(numA) && !isNaN(numB)) {
								return numA - numB;
							}

							return a.size.localeCompare(b.size);
						});
					return { ...product, sizes };
				})
				.filter((product) => product.sizes.length > 0),
		[products, availability]
	);

	const onToggleCheckProduct = useCallback((parentPartNumber: string) => {
		setProductsSelected((prev) => {
			const updatedSelection = { ...prev };

			if (!!Object.keys(prev).includes(parentPartNumber)) {
				delete updatedSelection[parentPartNumber];
			} else {
				updatedSelection[parentPartNumber] = null;
			}

			return updatedSelection;
		});
	}, []);

	const onSelectChildrenProduct = useCallback(
		(parentPartNumber: string, childPartNumber: string) => {
			setProductsSelected((prev) => {
				const updatedSelection = { ...prev };
				updatedSelection[parentPartNumber] = childPartNumber;
				return updatedSelection;
			});
		},
		[]
	);

	const totalSelectedAmount = useMemo(() => {
		if (Object.keys(productsSelected).length === 0) {
			return 0;
		}

		return Object.entries(productsSelected).reduce((acc, [parentPN, childrenPN]) => {
			const product = products.find((p) => p.partNumber === parentPN);

			if (!product) {
				return acc;
			}

			const childrenProduct = product.items?.find((item) => item.partNumber === childrenPN);

			const { prices } = getProductDisplayInfo(childrenProduct ?? product, product);
			const { offer = 0, list = 0 } = prices || {};

			const { price = 0 } = calculateProductPrices({
				list,
				offer,
				ribbons: childrenProduct?.ribbons ?? [],
			});

			return acc + price;
		}, 0);
	}, [productsSelected, products]);

	const isValidSizesSelection = useMemo(
		() => Object.entries(productsSelected).every(([, selectedPN]) => selectedPN !== null),
		[productsSelected]
	);

	const productSelectCount = useMemo(
		() => Object.keys(productsSelected).length,
		[productsSelected]
	);

	const isReadySelection = useMemo(() => {
		const selected = Object.entries(productsSelected);
		return selected.length > 0 && selected.every(([, childPartNumber]) => childPartNumber !== null);
	}, [productsSelected]);

	const addSelectedToCart = useCallback(async (): Promise<boolean> => {
		if (!isValidSizesSelection) {
			notifyError('No se han seleccionado todas las tallas');
			return false;
		}

		const orderItem: Array<any> = Object.entries(productsSelected)
			.map(([parentPN, childrenPN]) => {
				const product = products.find((p) => p.partNumber === parentPN);
				const childrenProduct = product?.items?.find((item) => item.partNumber === childrenPN);

				if (!product || !childrenProduct || !childrenPN) {
					return null;
				}

				return generateOrderItem({
					childrenProductId: childrenProduct.id,
					childrenPartNumber: childrenPN,
					quantity: 1,
					availability,
					storeName: settings.storeName,
					pickupInStoreShipMode,
				});
			})
			.filter((item) => item !== null);

		try {
			await addToCartFetcher(true)(
				settings?.storeId ?? '',
				{},
				{ ...BASE_ADD_2_CART_BODY, orderItem },
				params
			);

			if (couponCode) {
				try {
					await promoCodeApplicator(true)(settings?.storeId ?? '', couponCode, undefined, params);
				} catch (e) {
					console.log(processError(e as TransactionErrorResponse));
				}
			}

			await mutateCart();
			return true;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return false;
		}
	}, [
		isValidSizesSelection,
		productsSelected,
		products,
		availability,
		settings,
		pickupInStoreShipMode,
		couponCode,
		params,
		mutateCart,
		notifyError,
	]);

	const onAddToCart = useCallback(
		async (event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			const added = await addSelectedToCart();
			if (added) {
				onClose?.();
				router.push({ pathname: localRoutes.Cart.route.t() });
				scrollTo(0, 0);
			}
		},
		[addSelectedToCart, onClose, router, localRoutes.Cart.route]
	);

	const goToCheckoutCommon = useCallback(async () => {
		const added = await addSelectedToCart();
		if (added) {
			openAuthCheckoutFlow.off();
			onClose?.();
			router.push({ pathname: localRoutes.CheckOut.route.t() });
			scrollTo(0, 0);
		}
	}, [addSelectedToCart, openAuthCheckoutFlow, onClose, router, localRoutes.CheckOut.route]);

	const onAuthFlowClose = useCallback(() => {
		openAuthCheckoutFlow.off();
	}, [openAuthCheckoutFlow]);

	const onGoToCheckout = useCallback(
		async (event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			if (!isValidSizesSelection) {
				notifyError('No se han seleccionado todas las tallas');
				return;
			}

			if (!isLoggedIn) {
				openAuthCheckoutFlow.on();
				return;
			}

			await goToCheckoutCommon();
		},
		[isValidSizesSelection, isLoggedIn, openAuthCheckoutFlow, goToCheckoutCommon, notifyError]
	);

	useRemoveQueryParams([
		EMAIL_CAMPAIGN_QUERY_KEYS.partNumbers,
		EMAIL_CAMPAIGN_QUERY_KEYS.promoTitle,
		EMAIL_CAMPAIGN_QUERY_KEYS.contentConf,
	]);

	useEffect(() => {
		if (isLoadingProducts || isLoadingAvailability) {
			return;
		}

		const initialSelection: ProductSelection = {};
		productsWithSizesFiltered?.forEach((product) => {
			initialSelection[product.partNumber] = null;
		});
		setProductsSelected(initialSelection);
	}, [isLoadingProducts, isLoadingAvailability]); // eslint-disable-line react-hooks/exhaustive-deps

	const onGoToHome = useCallback(() => {
		router.push({ pathname: '/' });
	}, [router]);

	return {
		totalSelectedAmount,
		products: productsWithSizesFiltered,
		availability,
		isLoadingProducts,
		isLoadingAvailability,
		isValidSizesSelection,
		isReadySelection,
		productsSelected,
		productSelectCount,
		settings,
		onToggleCheckProduct,
		onSelectChildrenProduct,
		onAddToCart,
		onGoToCheckout,
		openAuthCheckoutFlow: openAuthCheckoutFlow.open,
		onAuthFlowClose,
		onAuthSuccess: goToCheckoutCommon,
		onGoToHome,
	};
};
